/**
 * `LINE_REQUIRED` — the one error the WhatsApp-only surfaces have to tell apart (K5).
 *
 * A tenant can have the WhatsApp channel on with no line connected yet. Templates, flows, the
 * playground, Instagram and message sending all genuinely need a line, so the backend answers
 * them **404 `{ code: 'LINE_REQUIRED' }`** — never 401/403. This module is what keeps that from
 * reading like a broken app: the clients throw a typed error instead of a raw message, and the
 * surfaces render one empty state (`LineRequiredEmptyState`) instead of a toast per request.
 *
 * No auth event, no logout: the session is perfectly valid, it just has nothing to send with.
 */

export const LINE_REQUIRED_CODE = 'LINE_REQUIRED';

export class LineRequiredError extends Error {
  readonly code = LINE_REQUIRED_CODE;

  constructor(message?: string) {
    super(message || 'A WhatsApp line is required for this operation');
    this.name = 'LineRequiredError';
  }
}

export function isLineRequiredError(error: unknown): error is LineRequiredError {
  return error instanceof LineRequiredError;
}

/**
 * Whether a non-OK response is the line-required verdict. Called from each client's error path
 * with the status and the already-read body text (the body is consumed once, so it is passed in
 * rather than re-read here).
 */
export function isLineRequiredResponse(status: number, bodyText: string): boolean {
  if (status !== 404) return false;
  try {
    return (JSON.parse(bodyText) as { code?: string }).code === LINE_REQUIRED_CODE;
  } catch {
    return false;
  }
}

/**
 * The error a client should throw for a non-OK response: the typed one when the body says
 * `LINE_REQUIRED`, otherwise null so the caller keeps its own error shape.
 */
export function lineRequiredErrorFor(status: number, bodyText: string): LineRequiredError | null {
  if (!isLineRequiredResponse(status, bodyText)) return null;
  let message: string | undefined;
  try {
    message = (JSON.parse(bodyText) as { message?: string }).message;
  } catch {
    message = undefined;
  }
  return new LineRequiredError(message);
}
